/** Minimal typings and the loader for YouTube's IFrame Player API. */

export interface PlayerVars {
  playsinline?: number;
  rel?: number;
  modestbranding?: number;
  controls?: number;
  disablekb?: number;
  iv_load_policy?: number;
  fs?: number;
  origin?: string;
}

export interface VideoRequest {
  videoId: string;
  startSeconds?: number;
}

export interface YTPlayer {
  loadVideoById(req: VideoRequest): void;
  cueVideoById(req: VideoRequest): void;
  playVideo(): void;
  pauseVideo(): void;
  stopVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  setVolume(percent: number): void;
  setPlaybackRate(rate: number): void;
  getCurrentTime(): number;
  getDuration(): number;
  destroy(): void;
}

export interface PlayerOptions {
  height: string;
  width: string;
  playerVars: PlayerVars;
  events: {
    onReady: () => void;
    onStateChange: (e: { data: number }) => void;
    onError: (e: { data: number }) => void;
  };
}

export interface YTNamespace {
  Player: new (mountId: string, options: PlayerOptions) => YTPlayer;
}

/** The player-state codes, inlined so no runtime lookup on the global is needed. */
export const YTState = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

interface YTGlobal {
  YT?: YTNamespace;
  onYouTubeIframeAPIReady?: () => void;
}

const API_URL = 'https://www.youtube.com/iframe_api';
const API_TIMEOUT = 15000;

let apiPromise: Promise<YTNamespace> | null = null;

/**
 * Loads the iframe API once per page. `loadScript` is injected so this stays
 * testable without reaching for the real network.
 */
export function loadIframeAPI(
  loadScript: (src: string, id: string) => Promise<void>,
): Promise<YTNamespace> {
  const w = globalThis as unknown as YTGlobal;
  apiPromise ??= new Promise<YTNamespace>((resolve, reject) => {
    if (w.YT?.Player) {
      resolve(w.YT);
      return;
    }
    const prev = w.onYouTubeIframeAPIReady;
    w.onYouTubeIframeAPIReady = () => {
      prev?.();
      if (w.YT) resolve(w.YT);
    };
    loadScript(API_URL, 'yt-iframe-api').catch(reject);
    setTimeout(() => {
      reject(new Error('YouTube player did not load'));
    }, API_TIMEOUT);
  });
  return apiPromise;
}
